import BaseController from './baseSystemController';
import eControllerTypes from './rules/eControllerTypes';
import { OP_WRAPPER_LOCALIZATION_PREFIX, SOCIAL_LANGUAGE_KEY } from '../../../utils/constants';
import ControllerSpinner from '../common/ControllerSpinner';
import URLs from '../../../api/URLs';

export default class ControllerRules extends BaseController {
  constructor({ container, ...props }) {
    super({ ...props });
    this._eButtonsTypes = {
      EBT_CLOSE: 'close_button',
    };
    this._eEventTypes = {
      CLOSE_CLICK: this.getEventName(this._eButtonsTypes.EBT_CLOSE),
    };
    this._eElementsTypes = {
      EET_OVERLAY: 'overlay',
      EET_MODAL_CONTAINER: 'modal_container',
      EET_TITLE: 'title',
      EET_CLOSE_BUTTON: this._eButtonsTypes.EBT_CLOSE,
      EET_DOWNLOAD_LINK: 'download_link',
      EET_CONTAINER_IFRAME: 'container_iframe',
      EET_CONTAINER_SPINNER: this.controllerTypes.ERCT_SPINNER,
    };
    this._eControllersClasses = {
      [this.controllerTypes.ERCT_SPINNER]: ControllerSpinner
    };

    this._eButtonsActions = {
      [this._eButtonsTypes.EBT_CLOSE]: this.hide.bind(this),
    };

    this._localizations = {
      [this._eElementsTypes.EET_TITLE]: `${OP_WRAPPER_LOCALIZATION_PREFIX}.rules.title`,
      [this._eElementsTypes.EET_DOWNLOAD_LINK]: `${OP_WRAPPER_LOCALIZATION_PREFIX}.rules.download`,
    }

    this._isSpinBlocker = true;

    this.basePdfUrl = URLs.getURL(URLs.eURLsNames.EUN_BASE_RULES_PDF);
    this.baseHtmlUrl = URLs.getURL(URLs.eURLsNames.EUN_BASE_RULES_HTML);
    this.baseFrameUrl = URLs.getURL(URLs.eURLsNames.EUN_BASE_FRAME_URL);
    this.fallbackLanguage = 'en';
    this._config = {
      bundle: null,
      lang: null,
    };

    this._getHtmlLink = this._getHtmlLink.bind(this);
    this._getPdfLink = this._getPdfLink.bind(this);

    this.init(container);
    this.hide();
  }

  async show() {
    super.show();
    this.controllers[this.controllerTypes.ERCT_SPINNER].show();

    try {
      await this._setFrame();
      this.controllers[this.controllerTypes.ERCT_SPINNER].hide();
    } catch (e) {
      if (typeof e === 'string') {
        console.warn(e);
        return;
      }

      console.error(`Can't load rules \nCertification: ${window.OPWrapperService.config.certificationName}\nGame: ${this._config.bundle}\nLanguages: ${this._config.lang}, ${this.fallbackLanguage}`);
    }
  }

  cleanup() {
    super.cleanup();
    this.controllers[this.controllerTypes.ERCT_SPINNER].hide();
    this.interactiveElements[this._eElementsTypes.EET_DOWNLOAD_LINK].style.setProperty('display', 'none');
    this._rejectFrame && this._rejectFrame('Aborted by user');
    this._abortRequest && this._abortRequest();
    if (this._frame) {
      this._frame.remove();
      this._frame = null;
    }
  }

  _setFrame() {
    return new Promise(async (resolve, reject) => {
      this._rejectFrame = reject;
      this._frame = document.createElement('iframe');
      this.interactiveElements[this._eElementsTypes.EET_CONTAINER_IFRAME].appendChild(this._frame);
      if (!this._frame) return reject();

      const html = await this._getUrl(this._getHtmlLink);
      let pdf;
      let frameUrl = html.frameUrl;
      let downloadUrl = html.frameUrl.concat('?download');

      if (!html.isAvailable) {
        pdf = await this._getUrl(this._getPdfLink);
        frameUrl = this._getFrameLink(pdf.frameUrl);
        downloadUrl = pdf.frameUrl.concat('?download');

        if (!pdf.isAvailable) return reject();
      }

      this._frame.src = frameUrl;

      if (pdf && pdf.isAvailable && !window.OPWrapperService.config.hideRulesDownloadButton) {
        this.interactiveElements[this._eElementsTypes.EET_DOWNLOAD_LINK].style.setProperty('display', 'block');
        this.interactiveElements[this._eElementsTypes.EET_DOWNLOAD_LINK].setAttribute('href', downloadUrl);
      }

      resolve();
    })
  }

  async _getUrl(method) {
    let url = method();
    let isAvailable = await this._checkIsUrlAvailable(url);

    if (!isAvailable && ![SOCIAL_LANGUAGE_KEY, this.fallbackLanguage].includes(this._config.lang)) {
      url = method(this.fallbackLanguage);
      isAvailable = await this._checkIsUrlAvailable(url);
    }

    return { isAvailable: isAvailable, frameUrl: url };
  }

  _getPdfLink(lang) {
    const certification = window.OPWrapperService.config.certificationName;
    return `${this.basePdfUrl}/${this._config.bundle}/${certification}/${lang || this._config.lang}.pdf`;
  }

  _getHtmlLink(lang) {
    const certification = window.OPWrapperService.config.certificationName;
    return `${this.baseHtmlUrl}/${certification}/${this._config.bundle}/${lang || this._config.lang}.html`;
  }

  _getFrameLink(pdfLink) {
    return `${this.baseFrameUrl}?url=${encodeURIComponent(pdfLink)}`;
  }

  async _checkIsUrlAvailable(url) {
    try {
      const abortController = new AbortController();
      this._abortRequest = abortController.abort.bind(abortController);
      const response = await fetch(url, {
        method: 'HEAD',
        signal: abortController.signal,
      });

      return response.status === 200;
    } catch (e) {
      return false;
    }
  }

  _afterInit() {
    super._afterInit();
    this._overlay = this.interactiveElements[this._eElementsTypes.EET_OVERLAY];
    this._modalContaier = this.interactiveElements[this._eElementsTypes.EET_MODAL_CONTAINER];
    this._overlay.onclick = (e) => {
      if (e.target === this._overlay) this.hide();
    };
  }

  _updateLocalizations(lang) {
    super._updateLocalizations(lang);
    this._config.lang = lang;
  }

  set config({ bundle, lang }) {
    this._config.bundle = bundle;
    this._config.lang = lang;
  }

  get controllerTypes() {
    return eControllerTypes;
  }

  set scaleData({ scaleData, rootScale }) {
    if (!this._mounted) return;
    this._scaleData = scaleData;

    this._modalContaier.dataset.orientation = scaleData.isPortrait ? 'portrait' : 'landscape';
    this._modalContaier.dataset.deviceType = scaleData.isMobile ? 'mobile' : 'desktop';

    for (const key in this.controllers) {
      const controller = this.controllers[key];
      controller.scaleData = { scaleData: this._scaleData };
    }
  }

  _getMarkup() {
    return `<div id=${this.interactiveElementsIds[this._eElementsTypes.EET_OVERLAY]} class="system-ui-container__modal_wrapper system-ui-container__modal__rules">
      <div id=${this.interactiveElementsIds[this._eElementsTypes.EET_MODAL_CONTAINER]} class="system-ui-container__modal_container system-ui-container__modal__rules__container">
        <div class="system-ui-container__modal_container__header">
          <span id=${this.interactiveElementsIds[this._eElementsTypes.EET_TITLE]}>
                  ${this._getLocalization(this._localizations[this._eElementsTypes.EET_TITLE])}
          </span>
          <button id=${this.interactiveElementsIds[this._eElementsTypes.EET_CLOSE_BUTTON]} class="system-ui-container__close_button system-ui-container__modal_container__header__close">
            <svg width="88" height="88" viewBox="0 0 88 88" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M66 22L22 66M22 22L66 66" stroke="white" stroke-width="6.28571" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div class="system-ui-container__modal__rules__container__body">
          <div class="system-ui-container__modal__rules__container__body__download_pdf">
            <a id=${this.interactiveElementsIds[this._eElementsTypes.EET_DOWNLOAD_LINK]}>
              ${this._getLocalization(this._localizations[this._eElementsTypes.EET_DOWNLOAD_LINK])}
            </a>
          </div>
          <div id=${this.interactiveElementsIds[this._eElementsTypes.EET_CONTAINER_IFRAME]} class="system-ui-container__modal__rules__container__body__container_frame">
            <div id=${this.interactiveElementsIds[this._eElementsTypes.EET_CONTAINER_SPINNER]}></div>
          </div>
        </div>
      </div>
    </div>`
  }
}
